import {useCallback, useEffect, useState} from 'react';
import type {
	CompetitorDiscoveryRun,
	CompetitorRelationType,
	CompetitorSet,
	CompetitorSetItem,
	OwnCardSuggestion,
	SellmonitorOAuthStatus,
	StartCompetitorDiscoveryResponse,
} from '@ozon-ai/sellmonitor/contracts';
import {api} from '../../../api/client';

function errorText(err: unknown, fallback: string): string {
	return err instanceof Error ? err.message : fallback;
}

function withoutItem(items: CompetitorSetItem[], dwhProductId: string): CompetitorSetItem[] {
	return items.filter((item) => item.dwhProductId !== dwhProductId);
}

function moveItem(
	current: CompetitorSet,
	item: CompetitorSetItem,
	relationType: CompetitorRelationType,
): CompetitorSet {
	const nextItem: CompetitorSetItem = {...item, status: relationType};
	const next: CompetitorSet = {
		...current,
		directCompetitors: withoutItem(current.directCompetitors, item.dwhProductId),
		closeAnalogues: withoutItem(current.closeAnalogues, item.dwhProductId),
		pendingReview: withoutItem(current.pendingReview, item.dwhProductId),
		excluded: withoutItem(current.excluded, item.dwhProductId),
	};
	if (relationType === 'direct_competitor') next.directCompetitors = [nextItem, ...next.directCompetitors];
	if (relationType === 'close_analogue') next.closeAnalogues = [nextItem, ...next.closeAnalogues];
	if (relationType === 'excluded') next.excluded = [nextItem, ...next.excluded];
	return next;
}

async function waitForRun(runId: string): Promise<CompetitorDiscoveryRun> {
	for (let attempt = 0; attempt < 40; attempt += 1) {
		const run = await api.get<CompetitorDiscoveryRun>(
			`/sellmonitor/competitor-discovery/run?runId=${encodeURIComponent(runId)}`,
		);
		if (run.status !== 'running' && run.status !== 'created') return run;
		await new Promise((resolve) => setTimeout(resolve, 1500));
	}
	throw new Error('Поиск занимает больше обычного. Обновите вкладку позже.');
}

export function useCompetitors(offerId: string) {
	const [competitorSet, setCompetitorSet] = useState<CompetitorSet | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [oauth, setOauth] = useState<SellmonitorOAuthStatus | null>(null);
	const [searching, setSearching] = useState(false);
	const [suggestions, setSuggestions] = useState<OwnCardSuggestion[]>([]);
	const [suggestionRunId, setSuggestionRunId] = useState<string | null>(null);
	const [searchNotice, setSearchNotice] = useState<string | null>(null);
	const [busyId, setBusyId] = useState<string | null>(null);

	const loadSet = useCallback(async () => {
		const next = await api.get<CompetitorSet>(
			`/sellmonitor/competitor-set?offerId=${encodeURIComponent(offerId)}`,
		);
		setCompetitorSet(next);
		return next;
	}, [offerId]);

	useEffect(() => {
		if (!offerId) return;
		let cancelled = false;
		setLoading(true);
		setError(null);
		Promise.all([
			loadSet(),
			api.get<SellmonitorOAuthStatus>('/sellmonitor/oauth/status').catch(() => null),
		])
			.then(([, status]) => {
				if (!cancelled) setOauth(status);
			})
			.catch((err: unknown) => {
				if (!cancelled) setError(errorText(err, 'Не удалось загрузить конкурентов'));
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [loadSet, offerId]);

	async function runSearch(phase: 'own_card' | 'candidates') {
		setSearching(true);
		setError(null);
		try {
			const started = await api.post<StartCompetitorDiscoveryResponse>(
				'/sellmonitor/competitor-discovery/run',
				{offerId, phase, force: true},
			);
			const run = started.status === 'running' || started.status === 'created'
				? await waitForRun(started.runId)
				: await api.get<CompetitorDiscoveryRun>(
					`/sellmonitor/competitor-discovery/run?runId=${encodeURIComponent(started.runId)}`,
				);
			if (phase === 'own_card') {
				setSuggestions(run.suggestions);
				setSuggestionRunId(run.id);
				setSearchNotice(
					run.suggestions.length === 0 && run.status !== 'failed'
						? run.warnings[0] || 'По этому названию карточки не найдены'
						: null,
				);
			}
			if (run.status === 'failed') setError(run.error || 'Поиск не выполнен');
			await loadSet();
		} catch (err: unknown) {
			setError(errorText(err, 'Не удалось запустить поиск'));
		} finally {
			setSearching(false);
		}
	}

	async function confirmMapping(dwhProductId: string) {
		const runId = suggestionRunId || competitorSet?.ownCardRunId;
		if (!runId) return;
		setBusyId(dwhProductId);
		setError(null);
		try {
			await api.post('/sellmonitor/competitor-discovery/mapping', {
				offerId,
				runId,
				dwhProductId,
			});
			setSuggestions([]);
			setSuggestionRunId(null);
			await loadSet();
		} catch (err: unknown) {
			setError(errorText(err, 'Не удалось подтвердить карточку'));
		} finally {
			setBusyId(null);
		}
	}

	async function classify(item: CompetitorSetItem, relationType: CompetitorRelationType) {
		if (!competitorSet) return;
		const previous = competitorSet;
		setCompetitorSet(moveItem(competitorSet, item, relationType));
		setBusyId(item.dwhProductId);
		setError(null);
		try {
			await api.post('/sellmonitor/competitor-relations', {
				offerId,
				candidateId: item.runId ? item.id : undefined,
				dwhProductId: item.dwhProductId,
				relationType,
			});
		} catch (err: unknown) {
			setCompetitorSet(previous);
			setError(errorText(err, 'Не удалось сохранить решение'));
		} finally {
			setBusyId(null);
		}
	}

	return {
		competitorSet,
		loading,
		error,
		oauth,
		searching,
		suggestions,
		searchNotice,
		busyId,
		findOwnCard: () => runSearch('own_card'),
		findCompetitors: () => runSearch('candidates'),
		confirmMapping,
		classify,
	};
}
